<script setup lang="ts">
import { ref, watch } from 'vue';
import BottomSheetModal from '@Components/BottomSheetModal.vue';
import AppFooter from '@Components/AppFooter.vue';
import { useCompanyStore } from '@Stores/company.store';
import { useDashboardStore } from '@Stores/dashboard.store';
import { CompanyLogoDisplay } from '@Shared';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';
import useDealLogoUrl from '@Use/useDealLogoUrl';
import type { Component } from 'vue';
import type { TaskItem } from '@Types/dashboard.types';

const props = defineProps<{
    open: boolean;
    task: TaskItem | null;
    view: Component | null;
}>();

const emit = defineEmits<{
    'update:open': [value: boolean];
}>();

// DS-5361 review: pin the Zendesk decision to the moment the sheet opens rather
// than reading the live prop. A mid-open status change can't actually reach us
// today — closeSheet() runs before loadDashboard(), and the store replaces task
// objects wholesale, so `pendingTask` keeps pointing at the old one — but a
// snapshot removes the question instead of relying on both of those holding.
const keepZendesk = ref(false);

watch(() => props.open, (open) => {
    if (open) keepZendesk.value = props.task?.status === 'completed';
}, { immediate: true });

const handleClose = () => emit('update:open', false);

// Same company/logo resolution as portal chrome (PortalHeader) — keeps the
// completion sheet left column visually consistent with the dashboard header.
const companyStore = useCompanyStore();
const dashboardStore = useDashboardStore();
const companyImageBaseUrl = useCompanyImageBaseUrl();

const companyLogoUrl = useDealLogoUrl(companyImageBaseUrl, companyStore, dashboardStore);
</script>

<template>
    <!-- Fullscreen variant covers the viewport; hideCloseOnDesktop suppresses
         the top-right × above 768px so the left column's back arrow is the only
         close affordance on desktop. Mobile keeps the × (no left column there).

         keepZendesk: a completed task renders a details view — a receipt the
         participant is reading, mirroring the route-level equivalents in
         request-mfe ("Your bank account details"), which keep the launcher.
         The only task this sheet opens while still active is fraud_education
         (DS-2860), where the sheet IS the task and the launcher covered its
         CTA — the original DS-2917 report. So: completed keeps, active hides. -->
    <BottomSheetModal
        :open="open"
        fullscreen
        hide-close-on-desktop
        :dialog-label="task?.title ?? 'Task completion'"
        :keep-zendesk="keepZendesk"
        @update:open="(val: boolean) => { if (!val) handleClose(); }"
    >
        <div v-if="view && task" class="tcs">
            <!-- Completion sheet left column — hidden below 768px via CSS -->
            <aside class="tcs__sidebar">
                <button
                    class="tcs__back portal-nav-btn"
                    type="button"
                    aria-label="Close completion view"
                    @click="handleClose"
                >
                    <app-icon icon="cd-ic-arrow-left" />
                </button>

                <div class="tcs__brand">
                    <CompanyLogoDisplay
                        v-if="companyStore.company"
                        class="tcs__brand-logo"
                        :company-logo-url="companyLogoUrl"
                        :company="companyStore.company"
                        :brand="companyStore.brand"
                    />
                    <span v-else class="tcs__brand-text">CertifID</span>
                </div>

                <!-- No default. The heading already names the outcome, so a badge here
                     only repeated it. Active-task flows still fill this with a
                     TaskProgressTracker. -->
                <slot name="sidebar-status" />
            </aside>

            <!-- Right column: content + reused AppFooter -->
            <main class="tcs__main">
                <div class="tcs__column">
                    <component :is="view" :task="task" />
                </div>
                <AppFooter />
            </main>
        </div>
    </BottomSheetModal>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.tcs {
    display: flex;
    flex-direction: column;
    min-height: 100%;

    @media (min-width: 768px) {
        flex-direction: row;
        background: $portal-surface;
    }

    // --- Left column (desktop only) ---
    &__sidebar {
        display: none;

        @media (min-width: 768px) {
            display: flex;
            flex-direction: column;
            flex-shrink: 0;
            width: 320px;
            padding: 64px 32px 24px 48px;
            gap: 40px;
        }

        @media (min-width: 1024px) {
            width: 396px;
            padding-left: 84px;
        }
    }

    &__back {
        flex-shrink: 0;
        width: 36px;
        height: 36px;
        // Always filled rather than only on hover. Transparent on the sheet's own
        // surface, it read as decoration rather than the way out.
        background: $portal-bg;

        :deep(svg) {
            width: 18px;
            height: 18px;
        }
    }

    &__brand {
        display: flex;
        align-items: center;
        min-width: 0;
    }

    &__brand-logo {
        max-width: 180px;
    }

    &__brand-text {
        font-size: $text-portal-h4;
        font-weight: 700;
        color: $portal-text;
    }

    // --- Main content column ---
    // Scrolling lives on the outer BottomSheetModal content (`.bsm__content`
    // owns `overflow-y: auto`). `.tcs__main` is a layout child — no own
    // scroll container — which avoids nested-scroll awkwardness and lets
    // the reused `<AppFooter>` stick to the bottom of the real scroll area.
    &__main {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-width: 0;

        // Content rides near the top of the right column — intentionally
        // diverging from the prototype's 144px so the hero doesn't start
        // below the sidebar's brand anchor. Less scroll to reach key info.
        @media (min-width: 768px) {
            padding-top: 32px;
        }
    }

    &__column {
        flex: 1;

        @media (min-width: 768px) {
            width: 100%;
            max-width: 480px;
            margin: 0 auto;
            padding: 0 0 96px;
        }
    }
}
</style>
